import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import {
  useLatestSms,
  useUnprocessedSms,
  useSmsStats,
  useMarkSmsProcessed,
  useMarkSmsUnprocessed,
} from "@/hooks/use-sms-relay";
import {
  IconRefresh,
  IconMessage,
  IconAlertCircle,
  IconCircleCheck,
  IconClock,
  IconDeviceMobile,
} from "@tabler/icons-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";

import { AppShell } from "@/components/layout/app-shell";
import { ProtectedRoute } from "@/components/layout/protected-route";

export const Route = createFileRoute("/sms-relay/")({
  component: SmsRelayPage,
});
function SmsRelayPage() {
  return (
    <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
      <AppShell>
        <SmsRelayPageContent />
      </AppShell>
    </ProtectedRoute>
  );
}


function SmsRelayPageContent() {
  const [activeTab, setActiveTab] = useState("latest");

  const { data: latestSms, isLoading: loadingLatest, refetch: refetchLatest } = useLatestSms(50);
  const { data: unprocessedSms, isLoading: loadingUnprocessed, refetch: refetchUnprocessed } =
    useUnprocessedSms(200);
  const { data: stats } = useSmsStats();
  const markProcessed = useMarkSmsProcessed();
  const markUnprocessed = useMarkSmsUnprocessed();

  const handleMarkProcessed = async (smsId: string) => {
    try {
      await markProcessed.mutateAsync(smsId);
      toast.success("SMS marked as processed");
    } catch (error: any) {
      toast.error(error.message || "Failed to mark SMS as processed");
    }
  };

  const handleMarkUnprocessed = async (smsId: string) => {
    try {
      await markUnprocessed.mutateAsync(smsId);
      toast.success("SMS marked as unprocessed");
    } catch (error: any) {
      toast.error(error.message || "Failed to mark SMS as unprocessed");
    }
  };

  const getOperatorColor = (operator: string) => {
    switch (operator) {
      case "bKash":
        return "text-pink-500";
      case "Nagad":
        return "text-orange-500";
      case "Rocket":
        return "text-purple-500";
      default:
        return "text-gray-500";
    }
  };

  const getMessageTypeVariant = (type: string) => {
    return type === "received" ? "default" : "secondary";
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">SMS Relay Monitoring</h1>
          <p className="text-muted-foreground mt-1">
            Real-time SMS message tracking and processing status
          </p>
        </div>
      </div>

      {/* Stats Summary */}
      {stats && (
        <div className="grid gap-4 md:grid-cols-4">
          <div className="rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-muted-foreground">
                Total SMS
              </p>
              <IconMessage className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-2xl font-bold mt-2">{(stats as any).totalSms || 0}</p>
          </div>
          <div className="rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-muted-foreground">
                Unprocessed
              </p>
              <IconAlertCircle className="h-4 w-4 text-orange-500" />
            </div>
            <p className="text-2xl font-bold mt-2">{(stats as any).unprocessedSms || 0}</p>
          </div>
          <div className="rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-muted-foreground">
                Processed Today
              </p>
              <IconCircleCheck className="h-4 w-4 text-green-500" />
            </div>
            <p className="text-2xl font-bold mt-2">{(stats as any).processedToday || 0}</p>
          </div>
          <div className="rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-muted-foreground">
                By Operator
              </p>
              <IconDeviceMobile className="h-4 w-4 text-blue-500" />
            </div>
            <div className="flex gap-2 mt-2">
              <Badge variant="outline" className="text-pink-500">
                bKash: {(stats as any).byOperator?.bKash || 0}
              </Badge>
              <Badge variant="outline" className="text-orange-500">
                Nagad: {(stats as any).byOperator?.Nagad || 0}
              </Badge>
              <Badge variant="outline" className="text-purple-500">
                Rocket: {(stats as any).byOperator?.Rocket || 0}
              </Badge>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="latest">
              Latest SMS
              {latestSms && (
                <Badge variant="secondary" className="ml-2">
                  {(latestSms as any)?.length || 0}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="unprocessed">
              Unprocessed
              {unprocessedSms && (unprocessedSms as any)?.length > 0 && (
                <Badge variant="destructive" className="ml-2">
                  {(unprocessedSms as any)?.length || 0}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>
          <Button
            variant="outline"
            onClick={() => {
              activeTab === "latest" ? refetchLatest() : refetchUnprocessed();
            }}
          >
            <IconRefresh className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Latest SMS Tab */}
        <TabsContent value="latest" className="mt-4">
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Time</TableHead>
                  <TableHead>Device</TableHead>
                  <TableHead>Phone Number</TableHead>
                  <TableHead>Operator</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Message</TableHead>
                  <TableHead>Transaction ID</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loadingLatest ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-8">
                      Loading SMS messages...
                    </TableCell>
                  </TableRow>
                ) : !latestSms || (latestSms as any)?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-8">
                      No SMS messages found
                    </TableCell>
                  </TableRow>
                ) : (
                  (Array.isArray(latestSms) ? latestSms : []).map((sms: any) => (
                    <TableRow key={sms.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <IconClock className="h-4 w-4 text-muted-foreground" />
                          {sms.timestamp
                            ? formatDistanceToNow(new Date(sms.timestamp), {
                                addSuffix: true,
                              })
                            : "Unknown"}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">{sms.deviceName}</div>
                      </TableCell>
                      <TableCell>
                        <div className="font-mono text-sm">
                          {sms.phoneNumber}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={getOperatorColor(sms.operator)}
                        >
                          {sms.operator}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={getMessageTypeVariant(sms.messageType)}>
                          {sms.messageType}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="max-w-md text-sm truncate">
                          {sms.message}
                        </div>
                      </TableCell>
                      <TableCell>
                        {sms.transactionId ? (
                          <div className="font-mono text-sm">
                            {sms.transactionId}
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-sm">
                            N/A
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {sms.amount ? (
                          <div>৳{sms.amount.toLocaleString("en-BD")}</div>
                        ) : (
                          <span className="text-muted-foreground text-sm">
                            N/A
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {sms.processed ? (
                          <Badge variant="default">
                            <IconCircleCheck className="h-3 w-3 mr-1" />
                            Processed
                          </Badge>
                        ) : (
                          <Badge variant="secondary">
                            <IconAlertCircle className="h-3 w-3 mr-1" />
                            Unprocessed
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {sms.processed ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMarkUnprocessed(sms.id)}
                          >
                            Mark Unprocessed
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMarkProcessed(sms.id)}
                          >
                            Mark Processed
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          {latestSms && (latestSms as any)?.length > 0 && (
            <p className="text-sm text-muted-foreground mt-2">
              Auto-refreshing every 10 seconds
            </p>
          )}
        </TabsContent>

        {/* Unprocessed SMS Tab */}
        <TabsContent value="unprocessed" className="mt-4">
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Time</TableHead>
                  <TableHead>Device</TableHead>
                  <TableHead>Phone Number</TableHead>
                  <TableHead>Operator</TableHead>
                  <TableHead>Message</TableHead>
                  <TableHead>Transaction ID</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loadingUnprocessed ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">
                      Loading unprocessed SMS...
                    </TableCell>
                  </TableRow>
                ) : !unprocessedSms || (unprocessedSms as any)?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">
                      <div className="flex flex-col items-center gap-2">
                        <IconCircleCheck className="h-12 w-12 text-green-500" />
                        <p className="font-medium">All SMS Processed!</p>
                        <p className="text-sm text-muted-foreground">
                          No unprocessed SMS messages found
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  (Array.isArray(unprocessedSms) ? unprocessedSms : []).map((sms: any) => (
                    <TableRow key={sms.id}>
                      <TableCell>
                        {formatDistanceToNow(new Date(sms.timestamp), {
                          addSuffix: true,
                        })}
                      </TableCell>
                      <TableCell>{sms.deviceName}</TableCell>
                      <TableCell>
                        <div className="font-mono">{sms.phoneNumber}</div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={getOperatorColor(sms.operator)}
                        >
                          {sms.operator}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="max-w-md truncate">{sms.message}</div>
                      </TableCell>
                      <TableCell>
                        {sms.transactionId ? (
                          <div className="font-mono text-sm">
                            {sms.transactionId}
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-sm">
                            N/A
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {sms.amount ? (
                          <div>৳{sms.amount.toLocaleString("en-BD")}</div>
                        ) : (
                          <span className="text-muted-foreground text-sm">
                            N/A
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleMarkProcessed(sms.id)}
                        >
                          Mark Processed
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          {unprocessedSms && (unprocessedSms as any)?.length > 0 && (
            <p className="text-sm text-muted-foreground mt-2">
              Auto-refreshing every 30 seconds
            </p>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
